import i18next from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import type { i18n as i18nType } from 'i18next';
import { writable } from 'svelte/store';
import dayjs, { getDayjsLocale } from '$lib/dayjs';
import type { I18nOverrides } from '$lib/utils/translationDictionary';

import { assembleSettingsTranslations } from './settings-translations';
import languages from './locales/languages.json';

let overrides: I18nOverrides = {};

type Resource = Record<string, unknown>;

const isPlainObject = (value: unknown): value is Resource =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const deepMerge = (base: Resource, override: Resource): Resource => {
	const merged: Resource = { ...base };
	for (const [key, value] of Object.entries(override)) {
		const current = merged[key];
		merged[key] = isPlainObject(current) && isPlainObject(value)
			? deepMerge(current, value)
			: value;
	}
	return merged;
};

// 三层，后者覆盖前者：locales/（上游原样）→ overrides/（我方，构建期）→ 管理员后台改的文案（上游，模块变量 overrides，见 loadResource）。
// locales/ 保持上游原样，自研文案放 overrides/，合并上游时零冲突。
// 只有 zh-CN / en-US 有 overrides，其余语种缺失的 key 由 fallbackLng 回落到 en-US。
export const loadBundledResource = async (language: string): Promise<Record<string, string>> => {
	const base = (await import(`./locales/${language}/translation.json`)).default;
	try {
		const override = (await import(`./overrides/${language}/translation.json`)).default;
		return deepMerge(base, override) as Record<string, string>;
	} catch {
		return base;
	}
};

const loadResource = async (language: string) =>
	assembleSettingsTranslations(
		await loadBundledResource(language),
		await loadBundledResource('en-US'),
		overrides[language],
		overrides['en-US']
	);

export const updateI18n = async (value: I18nOverrides = {}) => {
	overrides = value;
	const resources = await Promise.all(
		Object.keys(i18next.store?.data ?? {}).map(async (language) => ({
			language,
			resource: await loadResource(language)
		}))
	);
	for (const { language, resource } of resources) {
		i18next.removeResourceBundle(language, 'translation');
		i18next.addResourceBundle(language, 'translation', resource);
	}
	i18n.set(i18next);
};

const createI18nStore = (i18n: i18nType) => {
	const i18nWritable = writable(i18n);

	i18n.on('initialized', () => {
		i18nWritable.set(i18n);
	});
	i18n.on('loaded', () => {
		i18nWritable.set(i18n);
	});
	i18n.on('added', () => i18nWritable.set(i18n));
	i18n.on('languageChanged', (lang) => {
		dayjs.locale(getDayjsLocale(lang));
		i18nWritable.set(i18n);
		if (typeof document !== 'undefined') {
			document.documentElement.setAttribute('lang', lang);
		}
	});
	return i18nWritable;
};

const createIsLoadingStore = (i18n: i18nType) => {
	const isLoading = writable(false);

	// if loaded resources are empty || {}, set loading to true
	i18n.on('loaded', (resources) => {
		// console.log('loaded:', resources);
		isLoading.set(Object.keys(resources).length === 0);
	});

	// if resources failed loading, set loading to true
	i18n.on('failedLoading', () => {
		isLoading.set(true);
	});

	return isLoading;
};

const languageCodes = languages.map(({ code }) => code);

const toBundleCode = (code: string) => {
	const ownBundle = languageCodes.find((bundle) => bundle.toLowerCase() === code.toLowerCase());
	if (ownBundle) return ownBundle;
	const baseLanguage = code.split('-')[0].toLowerCase();
	const primaryBundle = `${baseLanguage}-${baseLanguage.toUpperCase()}`;
	if (languageCodes.includes(primaryBundle)) return primaryBundle;
	// the first sibling of a regional code can be another script (zh-HK would get zh-CN)
	if (code.includes('-')) return code;
	return languageCodes.find((bundle) => bundle.startsWith(`${baseLanguage}-`)) ?? code;
};

export const initI18n = (defaultLocale?: string, value: I18nOverrides = {}) => {
	overrides = value;
	const detector = new LanguageDetector();
	detector.addDetector({ name: 'defaultLocale', lookup: () => defaultLocale });

	return i18next
		.use(resourcesToBackend(loadResource))
		.use(detector)
		.init({
			debug: false,
			detection: {
				order: ['querystring', 'localStorage', 'defaultLocale', 'navigator'],
				caches: ['localStorage'],
				lookupQuerystring: 'lang',
				lookupLocalStorage: 'locale',
				convertDetectedLanguage: toBundleCode
			},
			fallbackLng: {
				fr: ['fr-FR'],
				default: ['en-US']
			},
			ns: 'translation',
			keySeparator: false,
			nsSeparator: false,
			returnEmptyString: false,
			interpolation: {
				escapeValue: false // not needed for svelte as it escapes by default
			}
		});
};

const i18n = createI18nStore(i18next);
const isLoadingStore = createIsLoadingStore(i18next);

export const getLanguages = async () => languages;
// Serialize switches so a slower download cannot overwrite a newer selection.
let languageChange = Promise.resolve();
export const changeLanguage = (lang: string) => {
	const change = languageChange.then(() => i18next.changeLanguage(toBundleCode(lang)));
	languageChange = change.then(
		() => {},
		() => {}
	);
	return change;
};

export default i18n;
export const isLoading = isLoadingStore;
